import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import type { Database } from '@klothiq/domain';

export type ProductType = Database['public']['Tables']['product_types']['Row'];
export type Attribute = Database['public']['Tables']['attributes']['Row'];
export type AttributeValue = Database['public']['Tables']['attribute_values']['Row'];
export type ProductTypeAttribute = Database['public']['Tables']['product_type_attributes']['Row'];
export type TargetAudience = Database['public']['Tables']['target_audiences']['Row'];
export type AgeGroup = Database['public']['Tables']['age_groups']['Row'];
export type SizeSet = Database['public']['Tables']['size_sets']['Row'];
export type Team = Database['public']['Tables']['teams']['Row'];
export type Category = Database['public']['Tables']['categories']['Row'];

export const PRODUCT_TYPES_KEY = 'product_types';
export const ATTRIBUTES_KEY = 'attributes';
export const ATTRIBUTE_VALUES_KEY = 'attribute_values';
export const PRODUCT_TYPE_ATTRIBUTES_KEY = 'product_type_attributes';
export const TARGET_AUDIENCES_KEY = 'target_audiences';
export const AGE_GROUPS_KEY = 'age_groups';
export const SIZE_SETS_KEY = 'size_sets';
export const TEAMS_KEY = 'teams';
export const CATEGORIES_KEY = 'categories';

// Default master seeds for fallback or initial population
export const DEFAULT_TARGET_AUDIENCES = [
  { code: 'MALE', name: 'Male', display_order: 1 },
  { code: 'FEMALE', name: 'Female', display_order: 2 },
  { code: 'UNISEX', name: 'Unisex', display_order: 3 },
];

export const DEFAULT_AGE_GROUPS = [
  { code: 'ADULT', name: 'Adult', display_order: 1 },
  { code: 'KIDS', name: 'Kids', display_order: 2 },
  { code: 'TODDLER', name: 'Toddler', display_order: 3 },
  { code: 'INFANT', name: 'Infant', display_order: 4 },
];

export const DEFAULT_SIZE_SETS = [
  { code: 'ADULT_STD', name: 'Adult Standard', sizes: ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'] },
  { code: 'KIDS_AGE', name: 'Kids Apparel (Age)', sizes: ['2-3Y', '3-4Y', '5-6Y', '7-8Y', '9-10Y', '11-12Y'] },
  { code: 'WAIST_INCH', name: 'Waist Size (Inches)', sizes: ['28', '30', '32', '34', '36', '38', '40'] },
];

export const DEFAULT_TEAMS = [
  { code: 'ARGENTINA', name: 'Argentina', sport: 'Football' },
  { code: 'BRAZIL', name: 'Brazil', sport: 'Football' },
  { code: 'PORTUGAL', name: 'Portugal', sport: 'Football' },
  { code: 'REAL_MADRID', name: 'Real Madrid', sport: 'Football' },
  { code: 'BARCELONA', name: 'FC Barcelona', sport: 'Football' },
  { code: 'INTER_MIAMI', name: 'Inter Miami', sport: 'Football' },
  { code: 'MAN_CITY', name: 'Manchester City', sport: 'Football' },
  { code: 'ARSENAL', name: 'Arsenal FC', sport: 'Football' },
];

export const DEFAULT_ATTRIBUTES = [
  { code: 'TEAM', name: 'Team', input_type: 'select', data_type: 'text', is_variant_level: false, display_order: 1 },
  { code: 'SLEEVE_TYPE', name: 'Sleeve Type', input_type: 'select', data_type: 'text', options: ['Half Sleeve', 'Full Sleeve', 'Sleeveless', '3/4 Sleeve'], is_variant_level: false, display_order: 2 },
  { code: 'MATERIAL', name: 'Material', input_type: 'select', data_type: 'text', options: ['Cotton', 'Polyester', 'Dri-FIT', 'Cotton Blend', 'Linen', 'Fleece', 'Denim'], is_variant_level: false, display_order: 3 },
  { code: 'FIT', name: 'Fit', input_type: 'select', data_type: 'text', options: ['Regular Fit', 'Slim Fit', 'Oversized', 'Relaxed Fit'], is_variant_level: false, display_order: 4 },
  { code: 'NECKLINE', name: 'Neckline', input_type: 'select', data_type: 'text', options: ['Crew Neck', 'V-Neck', 'Polo Collar', 'Round Neck', 'Henley'], is_variant_level: false, display_order: 5 },
  { code: 'COLLAR_TYPE', name: 'Collar Type', input_type: 'select', data_type: 'text', options: ['Spread Collar', 'Button-Down Collar', 'Mandarin Collar', 'Band Collar'], is_variant_level: false, display_order: 6 },
  { code: 'PATTERN', name: 'Pattern', input_type: 'select', data_type: 'text', options: ['Solid', 'Printed', 'Striped', 'Checked', 'Graphic'], is_variant_level: false, display_order: 7 },
  { code: 'SEASON', name: 'Season', input_type: 'select', data_type: 'text', options: ['2023/24', '2024/25', '2025/26', 'Summer', 'Winter', 'All Season'], is_variant_level: false, display_order: 8 },
  { code: 'COLOR', name: 'Color', input_type: 'select', data_type: 'text', options: ['Black', 'White', 'Blue', 'Red', 'Green', 'Navy', 'Yellow', 'Pink', 'Grey'], is_variant_level: true, display_order: 9 },
  { code: 'SIZE', name: 'Size', input_type: 'select', data_type: 'text', options: ['XS', 'S', 'M', 'L', 'XL', 'XXL'], is_variant_level: true, display_order: 10 },
];

export const DEFAULT_PRODUCT_TYPES = [
  { code: 'JERSEY', name: 'Jersey', description: 'Jerseys and Team Wear', display_order: 1, attrs: ['TEAM', 'SLEEVE_TYPE', 'MATERIAL', 'SEASON', 'COLOR', 'SIZE'] },
  { code: 'TSHIRT', name: 'T-Shirt', description: 'Casual T-Shirts and Graphic Tees', display_order: 2, attrs: ['SLEEVE_TYPE', 'NECKLINE', 'FIT', 'MATERIAL', 'PATTERN', 'COLOR', 'SIZE'] },
  { code: 'SHIRT', name: 'Shirt', description: 'Formal & Casual Button-Down Shirts', display_order: 3, attrs: ['SLEEVE_TYPE', 'COLLAR_TYPE', 'FIT', 'MATERIAL', 'PATTERN', 'COLOR', 'SIZE'] },
  { code: 'POLO', name: 'Polo Shirt', description: 'Classic Polo Collared Shirts', display_order: 4, attrs: ['SLEEVE_TYPE', 'FIT', 'MATERIAL', 'COLOR', 'SIZE'] },
  { code: 'HOODIE', name: 'Hoodie / Sweatshirt', description: 'Hoodies and Winter Sweatshirts', display_order: 5, attrs: ['FIT', 'MATERIAL', 'COLOR', 'SIZE'] },
  { code: 'TOP', name: 'Women\'s Top', description: 'Blouse and Tops for Females', display_order: 6, attrs: ['NECKLINE', 'SLEEVE_TYPE', 'FIT', 'MATERIAL', 'PATTERN', 'COLOR', 'SIZE'] },
  { code: 'SHORTS', name: 'Shorts', description: 'Sports and Casual Shorts', display_order: 7, attrs: ['FIT', 'MATERIAL', 'COLOR', 'SIZE'] },
  { code: 'PANTS', name: 'Pants / Trousers', description: 'Trousers, Jeans, and Track Pants', display_order: 8, attrs: ['FIT', 'MATERIAL', 'COLOR', 'SIZE'] },
];

// ── Target Audiences ───────────────────────────────────────────
export function useTargetAudiences() {
  const { organization } = useAuth();
  const orgId = organization?.id;

  return useQuery({
    queryKey: [TARGET_AUDIENCES_KEY, orgId],
    queryFn: async (): Promise<TargetAudience[]> => {
      const { data, error } = await supabase
        .from('target_audiences')
        .select('*')
        .eq('organization_id', orgId!)
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (error && (error.code === 'PGRST205' || error.message?.includes('does not exist'))) {
        return DEFAULT_TARGET_AUDIENCES as any;
      }
      if (error) throw error;
      if (!data || data.length === 0) {
        return DEFAULT_TARGET_AUDIENCES as any;
      }
      return data as TargetAudience[];
    },
    enabled: !!orgId,
  });
}

// ── Age Groups ─────────────────────────────────────────────────
export function useAgeGroups() {
  const { organization } = useAuth();
  const orgId = organization?.id;

  return useQuery({
    queryKey: [AGE_GROUPS_KEY, orgId],
    queryFn: async (): Promise<AgeGroup[]> => {
      const { data, error } = await supabase
        .from('age_groups')
        .select('*')
        .eq('organization_id', orgId!)
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (error && (error.code === 'PGRST205' || error.message?.includes('does not exist'))) {
        return DEFAULT_AGE_GROUPS as any;
      }
      if (error) throw error;
      if (!data || data.length === 0) {
        return DEFAULT_AGE_GROUPS as any;
      }
      return data as AgeGroup[];
    },
    enabled: !!orgId,
  });
}

// ── Size Sets ──────────────────────────────────────────────────
export function useSizeSets() {
  const { organization } = useAuth();
  const orgId = organization?.id;

  return useQuery({
    queryKey: [SIZE_SETS_KEY, orgId],
    queryFn: async (): Promise<SizeSet[]> => {
      const { data, error } = await supabase
        .from('size_sets')
        .select('*')
        .eq('organization_id', orgId!)
        .eq('is_active', true);

      if (error && (error.code === 'PGRST205' || error.message?.includes('does not exist'))) {
        return DEFAULT_SIZE_SETS as any;
      }
      if (error) throw error;
      if (!data || data.length === 0) {
        return DEFAULT_SIZE_SETS as any;
      }
      return data as SizeSet[];
    },
    enabled: !!orgId,
  });
}

// ── Teams ──────────────────────────────────────────────────────
export function useTeams() {
  const { organization } = useAuth();
  const orgId = organization?.id;

  return useQuery({
    queryKey: [TEAMS_KEY, orgId],
    queryFn: async (): Promise<Team[]> => {
      const { data, error } = await supabase
        .from('teams')
        .select('*')
        .eq('organization_id', orgId!)
        .eq('is_active', true)
        .order('name', { ascending: true });

      if (error && (error.code === 'PGRST205' || error.message?.includes('does not exist'))) {
        return DEFAULT_TEAMS as any;
      }
      if (error) throw error;
      if (!data || data.length === 0) {
        return DEFAULT_TEAMS as any;
      }
      return data as Team[];
    },
    enabled: !!orgId,
  });
}

// ── Query: list all product types ──────────────────────────────
export function useProductTypes() {
  const { organization } = useAuth();
  const orgId = organization?.id;

  return useQuery({
    queryKey: [PRODUCT_TYPES_KEY, orgId],
    queryFn: async (): Promise<ProductType[]> => {
      const { data, error } = await supabase
        .from('product_types')
        .select('*')
        .eq('organization_id', orgId!)
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (error && (error.code === 'PGRST205' || error.message?.includes('does not exist'))) {
        return DEFAULT_PRODUCT_TYPES as any;
      }
      if (error) throw error;
      if (!data || data.length === 0) {
        return DEFAULT_PRODUCT_TYPES as any;
      }
      return data as ProductType[];
    },
    enabled: !!orgId,
  });
}

// ── Mutation: create product type ──────────────────────────────
export function useCreateProductType() {
  const queryClient = useQueryClient();
  const { organization, profile } = useAuth();

  return useMutation({
    mutationFn: async (values: { name: string; code?: string; description?: string; display_order?: number }) => {
      const orgId = organization?.id;
      if (!orgId) throw new Error('Organization not found.');

      const code = values.code || values.name.toUpperCase().replace(/[^A-Z0-9]/g, '_');

      const { data, error } = await supabase
        .from('product_types')
        .insert({
          ...values,
          code,
          organization_id: orgId,
          created_by: profile?.id,
          updated_by: profile?.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PRODUCT_TYPES_KEY] });
    },
  });
}

// ── Mutation: update product type ──────────────────────────────
export function useUpdateProductType() {
  const queryClient = useQueryClient();
  const { profile } = useAuth();

  return useMutation({
    mutationFn: async ({ id, ...values }: Partial<ProductType> & { id: string }) => {
      const { data, error } = await supabase
        .from('product_types')
        .update({ ...values, updated_by: profile?.id })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PRODUCT_TYPES_KEY] });
    },
  });
}

// ── Query: list all attributes ─────────────────────────────────
export function useAttributes() {
  const { organization } = useAuth();
  const orgId = organization?.id;

  return useQuery({
    queryKey: [ATTRIBUTES_KEY, orgId],
    queryFn: async (): Promise<Attribute[]> => {
      const { data, error } = await supabase
        .from('attributes')
        .select('*')
        .eq('organization_id', orgId!)
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (error && (error.code === 'PGRST205' || error.message?.includes('does not exist'))) {
        return DEFAULT_ATTRIBUTES as any;
      }
      if (error) throw error;
      if (!data || data.length === 0) {
        return DEFAULT_ATTRIBUTES as any;
      }
      return data as Attribute[];
    },
    enabled: !!orgId,
  });
}

// ── Mutation: create attribute ─────────────────────────────────
export function useCreateAttribute() {
  const queryClient = useQueryClient();
  const { organization, profile } = useAuth();

  return useMutation({
    mutationFn: async (values: {
      name: string;
      code?: string;
      input_type: string;
      data_type?: string;
      options?: any;
      is_variant_level: boolean;
      display_order?: number;
    }) => {
      const orgId = organization?.id;
      if (!orgId) throw new Error('Organization not found.');

      const code = values.code || values.name.toUpperCase().replace(/[^A-Z0-9]/g, '_');

      const { data, error } = await supabase
        .from('attributes')
        .insert({
          ...values,
          code,
          organization_id: orgId,
          created_by: profile?.id,
          updated_by: profile?.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ATTRIBUTES_KEY] });
    },
  });
}

// ── Mutation: update attribute ─────────────────────────────────
export function useUpdateAttribute() {
  const queryClient = useQueryClient();
  const { profile } = useAuth();

  return useMutation({
    mutationFn: async ({ id, ...values }: Partial<Attribute> & { id: string }) => {
      const { data, error } = await supabase
        .from('attributes')
        .update({ ...values, updated_by: profile?.id })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ATTRIBUTES_KEY] });
    },
  });
}

function isMissingTableError(error: any): boolean {
  if (!error) return false;
  return (
    error.code === 'PGRST205' ||
    error.code === '42P01' ||
    error.code === '22P02' ||
    error.message?.includes('does not exist') ||
    error.message?.includes('schema cache') ||
    error.message?.includes('Could not find the table')
  );
}

const LOCAL_STORAGE_MAPPINGS_KEY = 'klothiq_product_type_attribute_mappings';

function getLocalAttributeMappings(productTypeId: string): any[] | null {
  try {
    const raw = localStorage.getItem(`${LOCAL_STORAGE_MAPPINGS_KEY}_${productTypeId}`);
    if (raw) return JSON.parse(raw);
  } catch (e) {
    // Ignore localStorage errors
  }
  return null;
}

function getFallbackDefaultMappings(productTypeId: string): any[] {
  const pt = DEFAULT_PRODUCT_TYPES.find(
    p => (p as any).id === productTypeId || p.code === productTypeId || p.name.toLowerCase() === productTypeId.toLowerCase()
  );
  if (pt) {
    const mapped = DEFAULT_ATTRIBUTES.filter(a => pt.attrs.includes(a.code));
    return mapped.map(a => ({
      ...a,
      id: a.code,
      is_required: a.code === 'TEAM' || a.code === 'SLEEVE_TYPE' || a.code === 'COLOR' || a.code === 'SIZE',
    }));
  }
  return [];
}

function saveLocalAttributeMapping(productTypeId: string, attributeId: string, is_required: boolean): void {
  try {
    const current = getLocalAttributeMappings(productTypeId) || getFallbackDefaultMappings(productTypeId);
    const existingIndex = current.findIndex((a: any) => a.id === attributeId || a.code === attributeId);
    let updated = [...current];
    if (existingIndex >= 0) {
      updated[existingIndex] = { ...updated[existingIndex], is_required };
    } else {
      const foundAttr = DEFAULT_ATTRIBUTES.find(a => a.code === attributeId || (a as any).id === attributeId);
      if (foundAttr) {
        updated.push({ ...foundAttr, id: foundAttr.code, is_required });
      } else {
        updated.push({ id: attributeId, code: attributeId, name: attributeId, is_required });
      }
    }
    localStorage.setItem(`${LOCAL_STORAGE_MAPPINGS_KEY}_${productTypeId}`, JSON.stringify(updated));
  } catch (e) {
    console.warn('LocalStorage save failed:', e);
  }
}

function deleteLocalAttributeMapping(productTypeId: string, attributeId: string): void {
  try {
    const current = getLocalAttributeMappings(productTypeId) || getFallbackDefaultMappings(productTypeId);
    const updated = current.filter((a: any) => a.id !== attributeId && a.code !== attributeId);
    localStorage.setItem(`${LOCAL_STORAGE_MAPPINGS_KEY}_${productTypeId}`, JSON.stringify(updated));
  } catch (e) {
    console.warn('LocalStorage delete failed:', e);
  }
}

export function isValidUUID(str?: string | null): boolean {
  if (!str) return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str);
}

// ── Query: get product type attributes ─────────────────────────
export function useProductTypeAttributes(productTypeId?: string) {
  return useQuery({
    queryKey: [PRODUCT_TYPE_ATTRIBUTES_KEY, productTypeId],
    queryFn: async () => {
      if (!productTypeId) return [];

      const local = getLocalAttributeMappings(productTypeId);
      if (!isValidUUID(productTypeId)) {
        return local || getFallbackDefaultMappings(productTypeId);
      }

      try {
        const { data, error } = await supabase
          .from('product_type_attributes')
          .select(`
            is_required,
            display_order,
            attributes (*)
          `)
          .eq('product_type_id', productTypeId);

        if (error && isMissingTableError(error)) {
          return local || getFallbackDefaultMappings(productTypeId);
        }
        if (error) throw error;

        if (!data || data.length === 0) {
          return local || getFallbackDefaultMappings(productTypeId);
        }

        // Transform the joined data
        return (data as any[])
          .filter(d => d.attributes && d.attributes.is_active !== false)
          .map(d => ({
            ...d.attributes,
            is_required: d.is_required,
            display_order: d.display_order ?? d.attributes.display_order ?? 0,
          })) as (Attribute & { is_required: boolean })[];
      } catch (e: any) {
        if (isMissingTableError(e)) {
          return local || getFallbackDefaultMappings(productTypeId);
        }
        throw e;
      }
    },
    enabled: !!productTypeId,
  });
}

// ── Mutation: map attribute to product type ─────────────────────
export function useMapAttributeToProductType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (values: { product_type_id: string; attribute_id: string; is_required: boolean; display_order?: number }) => {
      if (!isValidUUID(values.product_type_id) || !isValidUUID(values.attribute_id)) {
        saveLocalAttributeMapping(values.product_type_id, values.attribute_id, values.is_required);
        return { product_type_id: values.product_type_id, attribute_id: values.attribute_id, is_required: values.is_required };
      }

      try {
        const { data, error } = await supabase
          .from('product_type_attributes')
          .upsert(values)
          .select()
          .single();

        if (error) throw error;
        return data;
      } catch (err: any) {
        if (isMissingTableError(err)) {
          saveLocalAttributeMapping(values.product_type_id, values.attribute_id, values.is_required);
          return { product_type_id: values.product_type_id, attribute_id: values.attribute_id, is_required: values.is_required };
        }
        throw err;
      }
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: [PRODUCT_TYPE_ATTRIBUTES_KEY, vars.product_type_id] });
    },
  });
}

// ── Mutation: unmap attribute from product type ─────────────────
export function useUnmapAttributeFromProductType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (values: { product_type_id: string; attribute_id: string }) => {
      if (!isValidUUID(values.product_type_id) || !isValidUUID(values.attribute_id)) {
        deleteLocalAttributeMapping(values.product_type_id, values.attribute_id);
        return;
      }

      try {
        const { error } = await supabase
          .from('product_type_attributes')
          .delete()
          .eq('product_type_id', values.product_type_id)
          .eq('attribute_id', values.attribute_id);

        if (error) throw error;
      } catch (err: any) {
        if (isMissingTableError(err)) {
          deleteLocalAttributeMapping(values.product_type_id, values.attribute_id);
          return;
        }
        throw err;
      }
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: [PRODUCT_TYPE_ATTRIBUTES_KEY, vars.product_type_id] });
    },
  });
}

// ── Mutations for Teams, Size Sets, Target Audiences, Age Groups ─
export function useCreateTeam() {
  const queryClient = useQueryClient();
  const { organization, profile } = useAuth();

  return useMutation({
    mutationFn: async (values: { name: string; code?: string; sport?: string }) => {
      const orgId = organization?.id;
      if (!orgId) throw new Error('Organization not found.');
      const code = values.code || values.name.toUpperCase().replace(/[^A-Z0-9]/g, '_');

      const { data, error } = await supabase
        .from('teams')
        .insert({
          ...values,
          code,
          organization_id: orgId,
          created_by: profile?.id,
          updated_by: profile?.id,
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEAMS_KEY] });
    },
  });
}

export function useUpdateTeam() {
  const queryClient = useQueryClient();
  const { profile } = useAuth();

  return useMutation({
    mutationFn: async (values: { id: string; name: string; code?: string; sport?: string }) => {
      const { id, ...updates } = values;
      const { data, error } = await supabase
        .from('teams')
        .update({
          ...updates,
          updated_by: profile?.id,
        })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEAMS_KEY] });
    },
  });
}

export function useCreateTargetAudience() {
  const queryClient = useQueryClient();
  const { organization, profile } = useAuth();

  return useMutation({
    mutationFn: async (values: { name: string; code?: string; display_order?: number }) => {
      const orgId = organization?.id;
      if (!orgId) throw new Error('Organization not found.');
      const code = values.code || values.name.toUpperCase().replace(/[^A-Z0-9]/g, '_');

      const { data, error } = await supabase
        .from('target_audiences')
        .insert({
          ...values,
          code,
          organization_id: orgId,
          created_by: profile?.id,
          updated_by: profile?.id,
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TARGET_AUDIENCES_KEY] });
    },
  });
}

export function useUpdateTargetAudience() {
  const queryClient = useQueryClient();
  const { profile } = useAuth();

  return useMutation({
    mutationFn: async (values: { id: string; name: string; code?: string; display_order?: number }) => {
      const { id, ...updates } = values;
      const { data, error } = await supabase
        .from('target_audiences')
        .update({
          ...updates,
          updated_by: profile?.id,
        })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TARGET_AUDIENCES_KEY] });
    },
  });
}

export function useCreateAgeGroup() {
  const queryClient = useQueryClient();
  const { organization, profile } = useAuth();

  return useMutation({
    mutationFn: async (values: { name: string; code?: string; display_order?: number }) => {
      const orgId = organization?.id;
      if (!orgId) throw new Error('Organization not found.');
      const code = values.code || values.name.toUpperCase().replace(/[^A-Z0-9]/g, '_');

      const { data, error } = await supabase
        .from('age_groups')
        .insert({
          ...values,
          code,
          organization_id: orgId,
          created_by: profile?.id,
          updated_by: profile?.id,
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [AGE_GROUPS_KEY] });
    },
  });
}

export function useUpdateAgeGroup() {
  const queryClient = useQueryClient();
  const { profile } = useAuth();

  return useMutation({
    mutationFn: async (values: { id: string; name: string; code?: string; display_order?: number }) => {
      const { id, ...updates } = values;
      const { data, error } = await supabase
        .from('age_groups')
        .update({
          ...updates,
          updated_by: profile?.id,
        })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [AGE_GROUPS_KEY] });
    },
  });
}

export function useCreateSizeSet() {
  const queryClient = useQueryClient();
  const { organization, profile } = useAuth();

  return useMutation({
    mutationFn: async (values: { name: string; code?: string; sizes: string[] }) => {
      const orgId = organization?.id;
      if (!orgId) throw new Error('Organization not found.');
      const code = values.code || values.name.toUpperCase().replace(/[^A-Z0-9]/g, '_');

      const { data, error } = await supabase
        .from('size_sets')
        .insert({
          ...values,
          code,
          organization_id: orgId,
          created_by: profile?.id,
          updated_by: profile?.id,
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SIZE_SETS_KEY] });
    },
  });
}

export function useUpdateSizeSet() {
  const queryClient = useQueryClient();
  const { profile } = useAuth();

  return useMutation({
    mutationFn: async (values: { id: string; name: string; code?: string; sizes: string[] }) => {
      const { id, ...updates } = values;
      const { data, error } = await supabase
        .from('size_sets')
        .update({
          ...updates,
          updated_by: profile?.id,
        })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SIZE_SETS_KEY] });
    },
  });
}

// ── Query: list all categories ──────────────────────────────
export function useCategories() {
  const { organization } = useAuth();
  const orgId = organization?.id;

  return useQuery({
    queryKey: [CATEGORIES_KEY, orgId],
    queryFn: async (): Promise<Category[]> => {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .eq('organization_id', orgId!)
        .eq('is_active', true)
        .order('name', { ascending: true });

      if (error) return [];
      return (data as Category[]) ?? [];
    },
    enabled: !!orgId,
  });
}

// ─────────────────────────────────────────────
// FEAT-MASTER-CONFIG-DELETE-001 — Deactivate/Reactivate + Delete
// ─────────────────────────────────────────────

// ── "Manage" queries: ALL rows including inactive, for the Master Config
// admin page only. The plain useProductTypes()/useAttributes()/etc. above
// stay is_active-filtered (with DEFAULT_* fallback) everywhere else in the
// app — product form dropdowns, bulk-import validation — where an inactive
// or phantom-fallback row should never appear as a selectable option.
// Without these, deactivating something would make it vanish from the very
// admin list a user would need to find it again to reactivate (this is
// exactly how useAllStaffMembers already works for Staff — mirrored here
// for the other five entities). No DEFAULT_* fallback here either: for an
// admin management view, an empty table should show as empty, not as a
// phantom pre-populated list. ──

export function useAllProductTypes() {
  const { organization } = useAuth();
  const orgId = organization?.id;
  return useQuery({
    queryKey: [PRODUCT_TYPES_KEY, 'all', orgId],
    queryFn: async (): Promise<ProductType[]> => {
      const { data, error } = await supabase
        .from('product_types')
        .select('*')
        .eq('organization_id', orgId!)
        .order('display_order', { ascending: true });
      if (error) throw error;
      return (data as ProductType[]) ?? [];
    },
    enabled: !!orgId,
  });
}

export function useAllAttributes() {
  const { organization } = useAuth();
  const orgId = organization?.id;
  return useQuery({
    queryKey: [ATTRIBUTES_KEY, 'all', orgId],
    queryFn: async (): Promise<Attribute[]> => {
      const { data, error } = await supabase
        .from('attributes')
        .select('*')
        .eq('organization_id', orgId!)
        .order('display_order', { ascending: true });
      if (error) throw error;
      return (data as Attribute[]) ?? [];
    },
    enabled: !!orgId,
  });
}

export function useAllTargetAudiences() {
  const { organization } = useAuth();
  const orgId = organization?.id;
  return useQuery({
    queryKey: [TARGET_AUDIENCES_KEY, 'all', orgId],
    queryFn: async (): Promise<TargetAudience[]> => {
      const { data, error } = await supabase
        .from('target_audiences')
        .select('*')
        .eq('organization_id', orgId!)
        .order('display_order', { ascending: true });
      if (error) throw error;
      return (data as TargetAudience[]) ?? [];
    },
    enabled: !!orgId,
  });
}

export function useAllAgeGroups() {
  const { organization } = useAuth();
  const orgId = organization?.id;
  return useQuery({
    queryKey: [AGE_GROUPS_KEY, 'all', orgId],
    queryFn: async (): Promise<AgeGroup[]> => {
      const { data, error } = await supabase
        .from('age_groups')
        .select('*')
        .eq('organization_id', orgId!)
        .order('display_order', { ascending: true });
      if (error) throw error;
      return (data as AgeGroup[]) ?? [];
    },
    enabled: !!orgId,
  });
}

export function useAllSizeSets() {
  const { organization } = useAuth();
  const orgId = organization?.id;
  return useQuery({
    queryKey: [SIZE_SETS_KEY, 'all', orgId],
    queryFn: async (): Promise<SizeSet[]> => {
      const { data, error } = await supabase
        .from('size_sets')
        .select('*')
        .eq('organization_id', orgId!);
      if (error) throw error;
      return (data as SizeSet[]) ?? [];
    },
    enabled: !!orgId,
  });
}

export function useAllTeams() {
  const { organization } = useAuth();
  const orgId = organization?.id;
  return useQuery({
    queryKey: [TEAMS_KEY, 'all', orgId],
    queryFn: async (): Promise<Team[]> => {
      const { data, error } = await supabase
        .from('teams')
        .select('*')
        .eq('organization_id', orgId!)
        .order('name', { ascending: true });
      if (error) throw error;
      return (data as Team[]) ?? [];
    },
    enabled: !!orgId,
  });
}

// ── Deactivate / Reactivate toggles (fully reversible — is_active flip) ──

export function useSetProductTypeActive() {
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (values: { id: string; is_active: boolean }) => {
      const { data, error } = await supabase
        .from('product_types')
        .update({ is_active: values.is_active, updated_by: profile?.id })
        .eq('id', values.id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [PRODUCT_TYPES_KEY] }),
  });
}

export function useSetAttributeActive() {
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (values: { id: string; is_active: boolean }) => {
      const { data, error } = await supabase
        .from('attributes')
        .update({ is_active: values.is_active, updated_by: profile?.id })
        .eq('id', values.id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [ATTRIBUTES_KEY] }),
  });
}

export function useSetTargetAudienceActive() {
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (values: { id: string; is_active: boolean }) => {
      const { data, error } = await supabase
        .from('target_audiences')
        .update({ is_active: values.is_active, updated_by: profile?.id })
        .eq('id', values.id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [TARGET_AUDIENCES_KEY] }),
  });
}

export function useSetAgeGroupActive() {
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (values: { id: string; is_active: boolean }) => {
      const { data, error } = await supabase
        .from('age_groups')
        .update({ is_active: values.is_active, updated_by: profile?.id })
        .eq('id', values.id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [AGE_GROUPS_KEY] }),
  });
}

export function useSetSizeSetActive() {
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (values: { id: string; is_active: boolean }) => {
      const { data, error } = await supabase
        .from('size_sets')
        .update({ is_active: values.is_active, updated_by: profile?.id })
        .eq('id', values.id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [SIZE_SETS_KEY] }),
  });
}

export function useSetTeamActive() {
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (values: { id: string; is_active: boolean }) => {
      const { data, error } = await supabase
        .from('teams')
        .update({ is_active: values.is_active, updated_by: profile?.id })
        .eq('id', values.id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [TEAMS_KEY] }),
  });
}

// ── Tier A delete: no FK references either table anywhere in the schema —
// safe unconditional hard delete, same risk class as their existing
// create/update calls. See spec FR-001. ──

export function useDeleteTeam() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('teams').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [TEAMS_KEY] }),
  });
}

export function useDeleteSizeSet() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('size_sets').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [SIZE_SETS_KEY] }),
  });
}

// ── Tier B/C delete: usage-checked, role-gated, audited — via RPC.
// See spec FR-002 and supabase/migrations/00032_master_config_delete_rpc.sql. ──

export type DeletableMasterConfigEntity =
  | 'product_type'
  | 'attribute'
  | 'target_audience'
  | 'age_group'
  | 'staff_member';

const ENTITY_QUERY_KEY: Record<DeletableMasterConfigEntity, string> = {
  product_type: PRODUCT_TYPES_KEY,
  attribute: ATTRIBUTES_KEY,
  target_audience: TARGET_AUDIENCES_KEY,
  age_group: AGE_GROUPS_KEY,
  staff_member: 'staff_members',
};

export function useDeleteMasterConfigEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (values: { entity: DeletableMasterConfigEntity; id: string }) => {
      const { data, error } = await supabase.rpc('delete_master_config_entry', {
        p_entity: values.entity,
        p_id: values.id,
      });
      if (error) {
        const err = new Error(error.message || 'Failed to delete');
        Object.assign(err, error);
        throw err;
      }
      return data as { success: boolean; deleted_id: string; already_gone?: boolean };
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: [ENTITY_QUERY_KEY[vars.entity]] });
    },
  });
}
